import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Database, Filter, Search, ArrowLeft } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContentSources = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    search: ''
  });

  useEffect(() => {
    fetchSources();
  }, [filters.type, filters.status]);

  const fetchSources = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);

      const response = await axios.get(
        `${API}/admin/content-sources?${params.toString()}`,
        { withCredentials: true }
      );
      setSources(response.data);
    } catch (error) {
      console.error('Failed to fetch content sources:', error);
      toast.error('Failed to load content sources');
    } finally {
      setLoading(false);
    }
  };

  const filteredSources = sources.filter(source => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        source.title.toLowerCase().includes(searchLower) ||
        source.id.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const getStatusColor = (status) => {
    const colors = {
      uploaded: '#3A7BFF',
      live: '#FF4F4F',
      processing: '#FFA500',
      ready: '#29E07F',
      error: '#FF4F4F'
    };
    return colors[status] || '#8A93A6';
  };

  const getTypeIcon = (type) => {
    const icons = {
      upload: '📤',
      live: '🔴',
      replay: '🔄'
    };
    return icons[type] || '📄';
  };

  return (
    <div className="p-8 space-y-8" data-testid="content-sources-admin">
      <div className="flex items-center gap-4">
        <Link to="/admin" className="p-2 rounded-lg hover:bg-white/5 transition-colors">
          <ArrowLeft size={24} className="text-white" />
        </Link>
        <div className="flex-1">
          <h1 className="text-4xl font-bold text-white mb-2">Content Sources</h1>
          <p className="text-[#8A93A6] text-lg">Manage uploads, lives, and replays</p>
        </div>
        <Database size={32} className="text-[#3A7BFF]" />
      </div>

      {/* Filters */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Filter size={20} className="text-[#3A7BFF]" />
          <h2 className="text-lg font-bold text-white">Filters</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#8A93A6] mb-2">Type</label>
            <Select value={filters.type} onValueChange={(val) => setFilters({...filters, type: val})}>
              <SelectTrigger className="w-full" data-testid="filter-type">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="upload">Upload</SelectItem>
                <SelectItem value="live">Live</SelectItem>
                <SelectItem value="replay">Replay</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#8A93A6] mb-2">Status</label>
            <Select value={filters.status} onValueChange={(val) => setFilters({...filters, status: val})}>
              <SelectTrigger className="w-full" data-testid="filter-status">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="uploaded">Uploaded</SelectItem>
                <SelectItem value="live">Live</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="ready">Ready</SelectItem>
                <SelectItem value="error">Error</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#8A93A6] mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A93A6]" size={18} />
              <Input
                type="text"
                placeholder="Search by title or ID"
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content Sources List */}
      <div className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold text-white mb-6">
          {filteredSources.length} Content Source{filteredSources.length !== 1 ? 's' : ''}
        </h2>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-[#3A7BFF] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredSources.length === 0 ? (
          <div className="h-64 flex items-center justify-center border-2 border-dashed border-white/10 rounded-xl">
            <p className="text-[#8A93A6]">No content sources found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-[#8A93A6] font-medium">Type</th>
                  <th className="text-left py-3 px-4 text-[#8A93A6] font-medium">Title</th>
                  <th className="text-left py-3 px-4 text-[#8A93A6] font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-[#8A93A6] font-medium">Platform</th>
                  <th className="text-left py-3 px-4 text-[#8A93A6] font-medium">Duration</th>
                  <th className="text-left py-3 px-4 text-[#8A93A6] font-medium">Created</th>
                  <th className="text-left py-3 px-4 text-[#8A93A6] font-medium">ID</th>
                </tr>
              </thead>
              <tbody>
                {filteredSources.map((source) => (
                  <tr
                    key={source.id}
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    data-testid={`source-row-${source.id}`}
                  >
                    <td className="py-4 px-4">
                      <span className="text-2xl">{getTypeIcon(source.type)}</span>
                    </td>
                    <td className="py-4 px-4">
                      <div>
                        <p className="text-white font-medium">{source.title}</p>
                        {source.description && (
                          <p className="text-sm text-[#8A93A6] truncate max-w-xs">{source.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span
                        className="px-3 py-1 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: `${getStatusColor(source.status)}20`,
                          color: getStatusColor(source.status)
                        }}
                      >
                        {source.status}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-[#8A93A6]">{source.source_platform}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-[#8A93A6]">
                        {source.duration_seconds
                          ? `${Math.floor(source.duration_seconds / 60)}:${(source.duration_seconds % 60).toString().padStart(2, '0')}`
                          : '-'}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-[#8A93A6] text-sm">
                        {new Date(source.created_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <code className="text-xs text-[#8A93A6] bg-white/5 px-2 py-1 rounded">
                        {source.id.substring(0, 8)}
                      </code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentSources;
